import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  PRODUCTS as MOCK_PRODUCTS,
  FRAGRANCES_HER as MOCK_HER,
  FRAGRANCES_HIM as MOCK_HIM,
} from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Normalise an API product into the shape the existing UI expects.
 * The old mock had `image` (string). Backend adds `images` (array), `stock_status`,
 * `processing_time`. We keep the old keys working and expose the new ones.
 */
const shapeProduct = (p) => ({
  id: p.id,
  name: p.name,
  tagline: p.tagline,
  description: p.description,
  price: p.price,
  originalPrice: p.original_price ?? p.originalPrice,
  category: p.category,
  size: p.size,
  image: p.image,
  images: p.images || [],
  badge: p.badge,
  processing_time: p.processing_time || "3-5 business days",
  stock_status: p.stock_status || "in_stock",
  hasAudience: true,
});

// Simple module-level cache so switching pages doesn't re-fetch.
let _productsCache = null;
let _fragrancesCache = null;

export const useProducts = () => {
  const [products, setProducts] = useState(_productsCache ?? MOCK_PRODUCTS);
  const [loading, setLoading] = useState(_productsCache === null);

  const refetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      const shaped = (data || []).map(shapeProduct);
      _productsCache = shaped;
      setProducts(shaped);
    } catch (_e) {
      /* keep mock fallback */
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);
  return { products, loading, refetch };
};

export const useProduct = (productId) => {
  const [product, setProduct] = useState(() =>
    (_productsCache || MOCK_PRODUCTS).find((p) => p.id === productId) || null
  );
  const [loading, setLoading] = useState(!product);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products/${productId}`);
        if (!cancelled) setProduct(shapeProduct(data));
      } catch (_e) { /* keep fallback */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  return { product, loading };
};

export const useFragrances = () => {
  const [fragrances, setFragrances] = useState(
    _fragrancesCache ?? {
      her: MOCK_HER.map((f) => ({ ...f, stock_status: f.outOfStock ? "out_of_stock" : "in_stock" })),
      him: MOCK_HIM.map((f) => ({ ...f, stock_status: f.outOfStock ? "out_of_stock" : "in_stock" })),
    }
  );
  const [loading, setLoading] = useState(_fragrancesCache === null);

  const refetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/fragrances`);
      _fragrancesCache = data;
      setFragrances(data);
    } catch (_e) { /* fallback */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);
  return { fragrances, loading, refetch };
};

// Legacy adapter: some components import mock's FRAGRANCES arrays directly.
// Use this helper anywhere we want the flag mapped for the selector.
export const adaptFragrancesForSelector = (list) =>
  (list || []).map((f) => ({ ...f, outOfStock: f.stock_status === "out_of_stock" || f.outOfStock }));
